import type { ArtifactWithMeta, ThreadListItem } from '@freelancer-os/shared-types';
import { Button } from '@freelancer-os/ui/src/components/ui/button';
import { ScrollArea } from '@freelancer-os/ui/src/components/ui/scroll-area';
import { cn } from '@freelancer-os/ui/src/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { MessageSquare, Plus } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useChatStore } from '../../../stores/chatStore';
import { fetchApi } from '../../../lib/api';

export function ThreadSidebar() {
  const { t } = useTranslation('chat');
  const threadId = useChatStore((s) => s.threadId);
  const isStreaming = useChatStore((s) => s.isStreaming);
  const showPlaceholder = useChatStore((s) => s.showNewChatPlaceholder);
  const reset = useChatStore((s) => s.reset);

  const { data: threads = [], refetch } = useQuery<ThreadListItem[]>({
    queryKey: ['threads'],
    queryFn: () => fetchApi<ThreadListItem[]>('/threads'),
  });

  // Refetch thread list when threadId changes (new thread created)
  useEffect(() => {
    if (threadId) {
      refetch();
    }
  }, [threadId, refetch]);

  // Refetch after streaming ends to pick up generated title
  useEffect(() => {
    if (!isStreaming && threadId) {
      // Delay slightly — title generation is fire-and-forget on backend
      const timer = setTimeout(() => refetch(), 2000);
      return () => clearTimeout(timer);
    }
  }, [isStreaming, threadId, refetch]);

  const handleSelectThread = async (selectedThreadId: string) => {
    if (selectedThreadId === threadId) return;

    try {
      const data = await fetchApi<{
        messages: { role: string; content: string }[];
        artifacts?: ArtifactWithMeta[];
      }>(`/chat/threads/${selectedThreadId}`);

      // Convert API messages to ChatMessage format
      const chatMessages = (data.messages ?? []).map(
        (msg: { role: string; content: string }, i: number) => ({
          id: `loaded-${i}`,
          role: msg.role as 'user' | 'assistant',
          content: msg.content,
          timestamp: '',
        }),
      );

      // Atomic state replacement — no reset + loop race condition
      useChatStore.getState().loadThread(
        selectedThreadId,
        chatMessages,
        (data.artifacts ?? []) as ArtifactWithMeta[],
      );
    } catch (err) {
      console.error('Failed to load thread:', err);
    }
  };

  return (
    <div className="flex h-full w-64 flex-col border-r bg-muted/30">
      <div className="flex items-center justify-between p-3 border-b">
        <span className="text-sm font-medium">{t('threads.title')}</span>
        <Button variant="ghost" size="icon" onClick={reset} title="New chat">
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {showPlaceholder && !threadId && (
            <div className="w-full rounded-md px-3 py-2 text-left text-sm bg-muted/80 font-medium ring-1 ring-border">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate text-muted-foreground italic">{t('threads.newConversation')}</span>
              </div>
            </div>
          )}
          {threads.map((thread) => (
            <button
              key={thread.threadId}
              onClick={() => handleSelectThread(thread.threadId)}
              className={cn(
                'w-full rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-muted',
                thread.threadId === threadId && 'bg-muted/80 font-medium ring-1 ring-border',
              )}
            >
              <div className="flex items-center gap-2">
                <MessageSquare className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">
                  {thread.title || thread.lastMessage || t('threads.newConversation')}
                </span>
              </div>
              {thread.lastMessage && thread.title && (
                <p className="mt-0.5 truncate text-xs text-muted-foreground pl-5.5">
                  {thread.lastMessage}
                </p>
              )}
            </button>
          ))}
          {threads.length === 0 && !showPlaceholder && (
            <p className="px-3 py-6 text-center text-xs text-muted-foreground">
              {t('threads.empty')}
            </p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
